//#region IMPORT
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import WOR_BASIC_02 from './WOR_BASIC_02';
import { useSave1, useSave2, useSearch1 } from './WOR_BASIC_02_Query';
import { useSave } from '../../../Hooks/useSave';
import useFileUpload from '../../../Hooks/useFileUpload';
import { WOR_BASIC_02_Schema } from './WOR_BASIC_02_Schema';
import { useBackStack } from '../../../Hooks/back/useBackStack';
import { BackInfoActions } from '../../../Reducer/BackInfoReducer';
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import useFcmHandler from '../../../Hooks/useFcmHandler';
import Global from '../../../Services/Common/Global';
//#endregion

function M_WOR_BASIC_02F() {
  //#region 변수
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { gubun } = useParams();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((state) => state.searchInfo) ?? {};
  const gongjongList = searchInfo.gongjongList ?? [];
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const { workCd } = useSelector((state) => state.workingCheck);

  const [managerList, setManagerList] = useState([]);
  const detailItem = location.state?.detailItem ?? null;
  //#endregion

  //#region 커스텀훅
  const workInfoUpload = useFileUpload({
    oldFilePathField: 'imageFilePath',
    newFileNameField: 'saveName',
    getTargetPath: () => `AccidentStatus/${userInfo.custCd}/ACS_BASIC_02/Pre`,
    multiple: true,
    fileResultType: 'array',
  });
  // 스택형 뒤로가기로 변경: 기본 화면 이탈 시 바텀시트 체크 및 이전 화면으로 이동
  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom, // 바텀시트가 닫혀있을 때
    onBack: () => {
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClickWor' }));
    },
  });
  const sendFcm = useFcmHandler();
  //#endregion

  //#region 이벤트
  const titleName = useMemo(() => {
    if (gubun === 'risk') return '위험요인 제보';
    if (gubun === 'nearMiss') return '아차사고 상황';
    return '안전신문고';
  }, [gubun]);

  useEffect(() => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName(titleName));
  }, [dispatch, titleName]);

  // 수정 화면 진입 시 기존 첨부 이미지가 미리보기(previewList)에 보이도록 주입
  useEffect(() => {
    // 수정 대상이 없으면 초기화
    if (!detailItem) {
      workInfoUpload.clearFiles();
      return;
    }

    const images = Array.isArray(detailItem?.images) ? detailItem.images : [];

    // 업로드/저장 시 ImgType을 'PRE'로 고정하므로,
    // 기존 이미지도 PRE만 미리보기로 세팅
    const preImages = images
      .filter((img) => (img?.imgType ?? img?.ImgType) === 'PRE')
      .slice(0, 5);

    const BuildPreview = (img) => {
      const filePath = img?.imageFilePath ?? '';
      if (!filePath) return '';

      const normalized = String(filePath).replace(/\\/g, '/');
      return `${Global.file.useUrl}files/${normalized}`;
    };

    const mapped = preImages.map((img, idx) => {
      const imageFilePath = img?.imageFilePath ?? '';
      const saveNameFromPath =
        img?.saveName ??
        img?.imageSaveName ??
        img?.ImageSaveName ??
        (imageFilePath
          ? String(imageFilePath).split('/').pop()
          : `image_${idx}`);

      const fileNameFromPath =
        img?.fileName ??
        img?.imageFileName ??
        img?.ImageFileName ??
        saveNameFromPath;

      return {
        deleted: false,
        // 파일 업로드 훅(useFileUpload)이 사용하는 필드들
        fileName: fileNameFromPath ?? '',
        fileType: img?.fileType ?? img?.imageFileType ?? '',
        preview: BuildPreview(img),
        previewFilePath: imageFilePath,
        saveName: saveNameFromPath ?? '',

        // useFileUpload 옵션(oldFilePathField)에 맞춘 필드
        imageFilePath: imageFilePath,
      };
    });

    workInfoUpload.setPreviewList(mapped);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailItem]);
  //#endregion

  //#region 쿼리
  const { refetch: Search1 } = useSearch1({
    onSuccess: (rows) => {
      setManagerList(rows);
    },
  });
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();

  const isWorkEndFlow = location.state?.workEndYn === 'Y';

  const { run: Save1 } = useSave({
    menuId: 'WOR_BASIC_02',
    showSuccess: !isWorkEndFlow,
    schema: WOR_BASIC_02_Schema,
    saveData: (param) => param,
    fileSave: async () => {
      const alive = (workInfoUpload.previewList ?? []).filter(
        (i) => !i.deleted
      );

      // 기존 파일(이미 최종 경로에 존재)과 신규 파일(PreviewFiles)에 업로드된 파일을 분리
      const isNewPreview = (item) =>
        String(item?.previewFilePath ?? '').includes('PreviewFiles');

      const existingItems = alive.filter((i) => !isNewPreview(i));
      const newItems = alive.filter((i) => isNewPreview(i));

      // 신규 파일만 PreviewFiles -> 최종 경로로 이동
      const moved =
        newItems.length > 0 ? await workInfoUpload.SaveLogicFile(newItems) : [];

      // 기존 파일은 이동 없이 그대로 payload에 포함
      const existingImages = existingItems.map((i) => ({
        ImgType: 'PRE',
        ImageFileName: i.fileName ?? '',
        ImageFilePath: i.imageFilePath ?? i.previewFilePath ?? '',
        ImageSaveName: i.saveName ?? '',
      }));

      const movedImages = (moved || []).map((r) => ({
        ImgType: 'PRE',
        ImageFileName: r.fileName ?? '',
        ImageFilePath: r.filePath ?? '',
        ImageSaveName: r.saveName ?? '',
      }));

      return { images: [...existingImages, ...movedImages] };
    },
    requestSave: (rows) => Save1Async(rows),
    onSuccess: async () => {
      if (location.state?.workEndYn === 'Y') {
        await Save2();
      }
      console.log('===========================', managerList);

      managerList.forEach(async (manager) => {
        //fcm 알림요청
        await sendFcm({
          targetUserId: manager.id,
          body:
            gubun === 'risk'
              ? '위험요인 제보가 접수되었습니다. 확인해주세요.'
              : '아차사고 상황 제보가 접수되었습니다. 확인해주세요.',
          codeGroup: gubun === 'risk' ? 'AL_009_001' : 'AL_009_002',
          title: '안전신문고',
          route: '/M_WorkInfo',
        });
      });

      workInfoUpload.clearFiles();
      dispatch(BackInfoActions.clear());
      dispatch(
        BackInfoActions.push({ id: 'root_home', type: 'page', path: '/Home' })
      );
      navigate('/M_WorkInfo');
    },
  });

  // 작업 종료 저장
  const { run: Save2 } = useSave({
    menuId: 'WOR_BASIC_02',
    action: 'WORK_END',
    saveData: () => ({
      beforeComment: gubun === 'risk' ? '위험요인 제보.' : '아차사고 제보.',
      workCd,
    }),
    requestSave: (rows) => Save2Async(rows),
    onSuccess: async () => {
      for (const manager of managerList) {
        await sendFcm({
          targetUserId: manager.id,
          requestUserId: userInfo.userid,
          body: `${userInfo.userNm}님이 작업을 종료했습니다.`,
          status: '작업종료',
          codeGroup: 'AL_007',
          title: '작업종료',
          route: '/M_WorkEnd',
          responseData: workCd,
        });
      }
    },
  });
  //#endregion

  return (
    <div className="wrap">
      <WOR_BASIC_02
        gubun={gubun}
        gongjongList={gongjongList}
        Save1={Save1}
        workInfoUpload={workInfoUpload}
        detailItem={detailItem}
      />
    </div>
  );
}

export default M_WOR_BASIC_02F;
